import { useState } from 'react';
import { X, Flag } from 'lucide-react';
import { toast } from 'sonner';
import type { WPPost } from '../../services/wordpressApi';
import { useAppStore } from '../../stores/wpAppStore';

interface ReportModalProps {
  post: WPPost | null;
  onClose: () => void;
}

const REPORT_REASONS = ['Spam', 'Nội dung không phù hợp', 'Lừa đảo', 'Quấy rối', 'Khác'];

const styles = {
  backdrop: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    zIndex: 120,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
  },
  modal: {
    background: '#18181b',
    border: '1px solid #27272a',
    borderRadius: '24px',
    padding: '20px',
    maxWidth: '28rem',
    width: '100%',
    maxHeight: '85vh',
    overflowY: 'auto' as const,
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  title: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#fafafa',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  closeBtn: {
    padding: '4px',
    color: '#a1a1aa',
    background: 'none',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  reasons: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px',
    marginBottom: '16px',
  },
  reasonChip: {
    padding: '8px 14px',
    background: '#27272a',
    border: '1px solid #27272a',
    borderRadius: '9999px',
    fontSize: '13px',
    color: '#a1a1aa',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontFamily: 'inherit',
  },
  reasonChipActive: {
    background: 'rgba(239, 68, 68, 0.15)',
    border: '1px solid rgba(239, 68, 68, 0.5)',
    color: '#f87171',
  },
  textarea: {
    width: '100%',
    padding: '12px 16px',
    background: '#27272a',
    border: '1px solid #27272a',
    borderRadius: '12px',
    color: '#fafafa',
    fontSize: '14px',
    outline: 'none',
    resize: 'none' as const,
    minHeight: '80px',
    transition: 'border-color 0.2s',
    fontFamily: 'inherit',
    marginBottom: '16px',
    boxSizing: 'border-box' as const,
  },
  actions: {
    display: 'flex',
    gap: '12px',
    paddingTop: '16px',
    borderTop: '1px solid #27272a',
  },
  cancelBtn: {
    flex: 1,
    padding: '12px 20px',
    background: '#27272a',
    color: '#a1a1aa',
    border: 'none',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  submitBtn: {
    flex: 1,
    padding: '12px 20px',
    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  error: {
    fontSize: '13px',
    color: '#ef4444',
    margin: '0 0 12px 0',
  },
};

export default function ReportModal({ post, onClose }: ReportModalProps) {
  const reportPost = useAppStore((s) => s.reportPost);
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!post) return null;

  const handleClose = () => {
    if (submitting) return;
    setDetails('');
    setReason('');
    onClose();
  };

  const handleSubmit = async () => {
    if (submitting) return;
    const finalReason = reason || details.trim() || 'Không rõ lý do';
    setSubmitting(true);
    try {
      await reportPost(post.id, finalReason);
      toast.success('Đã gửi báo cáo. Cảm ơn bạn!');
      handleClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Không thể gửi báo cáo. Vui lòng thử lại.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.backdrop} onClick={handleClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>
            <Flag size={18} color="#f87171" />
            Báo cáo bài viết
          </h3>
          <button
            style={styles.closeBtn}
            onClick={handleClose}
            disabled={submitting}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#3f3f46';
              e.currentTarget.style.color = '#fafafa';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none';
              e.currentTarget.style.color = '#a1a1aa';
            }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={styles.reasons}>
          {REPORT_REASONS.map((r) => (
            <button
              key={r}
              style={reason === r ? { ...styles.reasonChip, ...styles.reasonChipActive } : styles.reasonChip}
              onClick={() => setReason(reason === r ? '' : r)}
            >
              {r}
            </button>
          ))}
        </div>

        <textarea
          style={styles.textarea}
          placeholder="Mô tả chi tiết lý do báo cáo (tùy chọn)..."
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          onFocus={(e) => (e.currentTarget.style.borderColor = '#ef4444')}
          onBlur={(e) => (e.currentTarget.style.borderColor = '#27272a')}
        />

        <div style={styles.actions}>
          <button style={styles.cancelBtn} onClick={handleClose} disabled={submitting}>
            Hủy
          </button>
          <button
            style={{
              ...styles.submitBtn,
              opacity: submitting ? 0.5 : 1,
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Đang gửi...' : 'Gửi báo cáo'}
          </button>
        </div>
      </div>
    </div>
  );
}